import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config";

const normalizeText = (value = "") =>
  String(value).toLowerCase().replace(/\s+/g, " ").trim();

const toSlug = (value = "") =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-");

const normalizeImage = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${API_BASE_URL}/${String(path).replace(/^\/+/, "")}`;
};

const buildSearchIndex = (products = []) =>
  products.map((item) => {
    const categoryName =
      typeof item.category === "object"
        ? item.category?.name || ""
        : item.categoryName || "";

    const displayWeight = item.net_wt || item.netWt || item.weight || "250g";

    const resolvedSlug =
      item.slug ||
      item.seoSlug ||
      item.urlSlug ||
      item._id ||
      item.id ||
      toSlug(item.name);

    const searchable = normalizeText(
      [
        item.name,
        categoryName,
        item.shortDescription,
        item.description,
        displayWeight,
        Array.isArray(item.tags) ? item.tags.join(" ") : "",
      ]
        .filter(Boolean)
        .join(" "),
    );

    return {
      ...item,
      _key: item._id || item.id || resolvedSlug,
      img: normalizeImage(item.mainImage || item.image || item.img),
      categoryName,
      displayWeight,
      slug: resolvedSlug,
      searchable,
    };
  });

const scoreItem = (item, query) => {
  const q = normalizeText(query);
  if (!q) return 0;

  let score = 0;
  const name = normalizeText(item.name);
  const category = normalizeText(item.categoryName);

  if (name === q) score += 100;
  if (name.startsWith(q)) score += 50;
  if (name.includes(q)) score += 30;
  if (category.includes(q)) score += 15;
  if (item.searchable.includes(q)) score += 10;

  q.split(" ").forEach((word) => {
    if (!word) return;
    if (name.includes(word)) score += 8;
    if (category.includes(word)) score += 4;
    if (item.searchable.includes(word)) score += 2;
  });

  return score;
};

const getSuggestions = (products, query) => {
  const q = normalizeText(query);
  if (!q) return [];

  const unique = new Map();

  products.forEach((item) => {
    [item.name, item.categoryName].filter(Boolean).forEach((text) => {
      const normalized = normalizeText(text);
      if (normalized.includes(q) && !unique.has(normalized)) {
        unique.set(normalized, text);
      }
    });
  });

  return Array.from(unique.values()).slice(0, 6);
};

export default function SearchDropdown({ isOpen, onClose }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const hasFetchedRef = useRef(false);

  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1200);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Lock body scroll when search is open on mobile
  useEffect(() => {
    if (isOpen && isMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, isMobile]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);

        const res = await fetch(`${API_BASE_URL}/api/content/products`);
        const json = await res.json();

        const activeProducts = Array.isArray(json?.data)
          ? json.data.filter(
              (item) =>
                !item.isDeleted &&
                ["Published", "Public"].includes(item.status),
            )
          : [];

        setProducts(buildSearchIndex(activeProducts));
        hasFetchedRef.current = true;
      } catch (error) {
        console.error("Search fetch failed:", error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && !hasFetchedRef.current) {
      fetchProducts();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 80);

    return () => clearTimeout(timer);
  }, [isOpen]);

  const matchedProducts = useMemo(() => {
    if (!query.trim()) return [];

    return [...products]
      .map((item) => ({ ...item, score: scoreItem(item, query) }))
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 6);
  }, [products, query]);

  const suggestions = useMemo(() => {
    return getSuggestions(products, query);
  }, [products, query]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;

    onClose?.();
    navigate(`/search/${toSlug(trimmed)}`);
  };

  const handleSuggestionClick = (value) => {
    onClose?.();
    navigate(`/search/${toSlug(value)}`);
  };

  const handleProductClick = () => {
    onClose?.();
  };

  const handleClear = () => {
    setQuery("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      onClose?.();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`bd-search-dropdown-container ${isMobile ? "is-mobile-search" : ""}`}
      onMouseDown={(e) => {
        e.nativeEvent.stopImmediatePropagation();
        e.stopPropagation();
      }}
      onClick={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <form onSubmit={handleSubmit}>
        <div className="bd-search-dropdown-top">
          {isMobile ? (
            <button
              type="button"
              className="back-btn"
              onClick={onClose}
              aria-label="Back"
            >
              <i className="fa-regular fa-arrow-left"></i>
            </button>
          ) : (
            <i className="fa-regular fa-magnifying-glass search-icon"></i>
          )}

          <input
            ref={inputRef}
            type="text"
            className="search-input"
            placeholder="Search sweets, snacks, bakery..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            role="combobox"
            aria-expanded="true"
            aria-autocomplete="list"
            aria-label="Search products"
            autoComplete="off"
          />

          {query ? (
            <button
              type="button"
              className="clear-btn p-1"
              onClick={handleClear}
              aria-label="Clear search"
            >
              <i className="far fa-times"></i>
            </button>
          ) : null}
        </div>

        <div className="bd-search-dropdown-body">
          <div className="bd-search-dropdown-side">
            <h6>Suggestions</h6>

            {!query ? (
              <div className="placeholder-text">
                {loading ? "Loading products..." : "Start typing to search"}
              </div>
            ) : suggestions.length ? (
              <div className="suggestion-list">
                {suggestions.map((item, index) => (
                  <button
                    key={`${item}-${index}`}
                    type="button"
                    className="suggestion-row"
                    onClick={() => handleSuggestionClick(item)}
                  >
                    <span>{item}</span>
                    <i className="fa-regular fa-arrow-right"></i>
                  </button>
                ))}
              </div>
            ) : (
              <div className="placeholder-text">No suggestions</div>
            )}
          </div>

          <div className="bd-search-dropdown-main">
            <h6>Products</h6>

            {!query ? (
              <div className="placeholder-text">
                Matching products will appear here
              </div>
            ) : matchedProducts.length ? (
              <div className="product-list">
                {matchedProducts.map((product) => (
                  <Link
                    key={product._key}
                    to={`/product/${product.slug}`}
                    className="product-row"
                    onClick={handleProductClick}
                  >
                    <div className="thumb">
                      <img
                        src={
                          product.img ||
                          "/assets/img/a1-img/product-placeholder.png"
                        }
                        alt={product.name}
                        loading="lazy"
                        width="100"
                        height="70"
                      />
                    </div>

                    <div className="meta">
                      <div className="category">
                        {product.categoryName || "Product"}
                      </div>
                      <div className="name">{product.name}</div>
                      <div className="weight">{product.displayWeight}</div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="placeholder-text">No matching products</div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
